import { SignIn, useAuth } from "@mattfinkel/common-web";
import type { ReactNode } from "react";
import { Route, Routes, useLocation } from "react-router";
import { Layout } from "./components/Layout";
import { auth, googleProvider } from "./firebase";
import Arenas from "./pages/Arenas";
import Events from "./pages/Events";
import NotFound from "./pages/NotFound";
import People from "./pages/People";
import Share from "./pages/Share";
import Stats from "./pages/Stats";
import Upcoming from "./pages/Upcoming";

/** The centred card the app shows before it has an authorized session. */
function Gate({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-8">
      <div className="w-full max-w-md space-y-6">
        <header>
          <h1 className="font-display text-3xl font-bold uppercase">
            Sports<span className="text-gold">·</span>Tracker
          </h1>
          <p className="text-sm text-muted">Every game you've been to</p>
        </header>
        {children}
      </div>
    </div>
  );
}

export default function App() {
  const { state, signOut, retry } = useAuth();
  const { pathname, search } = useLocation();

  // A share link is PUBLIC: it renders before, and regardless of, the auth
  // state, with its own token as the only credential (verified server-side).
  // `/?share=<token>` is an older link form kept working alongside
  // `/share/:token`.
  const legacyShare = pathname === "/" ? new URLSearchParams(search).get("share") : null;
  if (pathname.startsWith("/share/") || legacyShare) {
    return (
      <Routes>
        <Route path="/share/:token" element={<Share />} />
        <Route path="*" element={<Share token={legacyShare ?? ""} />} />
      </Routes>
    );
  }

  // Routes are mounted ONLY inside the allowed branch, so no page component
  // renders for an unauthorized session even on a deep link. The backend
  // enforces the same allowlist on every request regardless.
  if (state.status === "allowed") {
    return (
      <Routes>
        <Route element={<Layout email={state.user.email} onSignOut={signOut} />}>
          <Route index element={<Events />} />
          <Route path="upcoming" element={<Upcoming />} />
          <Route path="stats" element={<Stats />} />
          <Route path="arenas" element={<Arenas />} />
          <Route path="people" element={<People />} />
          {/* Hosting's SPA catch-all serves index.html for any unknown path,
              so an unmatched deep link arrives here rather than as a CDN 404. */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Gate>
      {state.status === "idle" && <SignIn auth={auth} provider={googleProvider} />}
      {state.status === "verifying" && (
        <div className="rounded-lg border border-line bg-surface p-4 text-sm">Verifying access…</div>
      )}
      {/* "Could not ask" is NOT "denied": a cold start or a transient 5xx keeps
          the session and offers a retry instead of signing a legitimate user out. */}
      {state.status === "unavailable" && (
        <div className="space-y-3">
          <div className="rounded-lg border border-soon/50 bg-soon/10 p-4 text-sm">Could not reach the server: {state.reason}</div>
          <button onClick={retry} className="rounded-lg bg-neutral-100 px-4 py-2 text-ground">
            Try again
          </button>
        </div>
      )}
      {state.status === "denied" && (
        <div className="space-y-3">
          <div className="rounded-lg border border-bad/50 bg-bad/10 p-4 text-sm">Access denied: {state.reason}</div>
          <SignIn auth={auth} provider={googleProvider} />
        </div>
      )}
    </Gate>
  );
}
